import type { Consignment, MaritimeReferenceKind, SeaTrackingType } from '../../lib/api/types'

const CONTAINER_REFERENCE_PATTERN = /^[A-Z]{4}\d{7}$/

type SeaShipmentTrackingSource = {
  reference?: string | null
  tracking_type?: SeaTrackingType | null
  reference_kind?: MaritimeReferenceKind | null
  carrier_scac?: string | null
  booking_number?: string | null
  master_bill_of_lading_number?: string | null
  house_bill_of_lading_number?: string | null
  containers?: Array<{ container_number?: string | null }>
}

type TrackingConsignment = Pick<
  Consignment,
  'international_tracking_number' | 'tracking_number'
> & {
  sea_shipments?: SeaShipmentTrackingSource[]
}

export type ConsignmentSeaTrackingTarget = {
  reference: string
  trackingType: SeaTrackingType
  referenceKind: MaritimeReferenceKind | null
  carrierScac: string | null
}

export function trackingTypeForReferenceKind(
  referenceKind: MaritimeReferenceKind,
): SeaTrackingType {
  if (referenceKind === 'CONTAINER') return 'container'
  if (referenceKind === 'BOOKING') return 'booking'
  return 'bill_of_lading'
}

export function referenceKindForTrackingType(
  trackingType: SeaTrackingType,
): MaritimeReferenceKind | null {
  if (trackingType === 'container') return 'CONTAINER'
  if (trackingType === 'booking') return 'BOOKING'
  if (trackingType === 'bill_of_lading') return 'MASTER_BILL_OF_LADING'
  return null
}

function typedShipmentReference(shipment: SeaShipmentTrackingSource) {
  if (shipment.reference_kind === 'MASTER_BILL_OF_LADING') {
    return shipment.master_bill_of_lading_number?.trim() || shipment.reference?.trim() || null
  }
  if (shipment.reference_kind === 'HOUSE_BILL_OF_LADING') {
    return shipment.house_bill_of_lading_number?.trim() || shipment.reference?.trim() || null
  }
  if (shipment.reference_kind === 'BOOKING') {
    return shipment.booking_number?.trim() || shipment.reference?.trim() || null
  }
  return shipment.reference?.trim() || null
}

export function getConsignmentSeaTrackingTarget(
  consignment: TrackingConsignment,
): ConsignmentSeaTrackingTarget | null {
  const firstSeaShipment = consignment.sea_shipments?.find(
    (shipment) =>
      typedShipmentReference(shipment) ||
      shipment.containers?.some((container) => container.container_number?.trim()),
  )

  if (firstSeaShipment) {
    const firstContainer =
      firstSeaShipment.containers
        ?.find((container) => isContainerReference(container.container_number))
        ?.container_number?.trim()
        .toUpperCase() || null
    const referenceKind = firstSeaShipment.reference_kind ?? null
    const shipmentReference = typedShipmentReference(firstSeaShipment)
    const declaredTrackingType =
      firstSeaShipment.tracking_type ??
      (referenceKind ? trackingTypeForReferenceKind(referenceKind) : null)

    if (shipmentReference) {
      const normalizedReference = shipmentReference.toUpperCase()
      const trackingType =
        declaredTrackingType === 'container' && !isContainerReference(normalizedReference)
          ? referenceKind
            ? trackingTypeForReferenceKind(referenceKind)
            : 'booking'
          : (declaredTrackingType ??
            (isContainerReference(normalizedReference) ? 'container' : 'booking'))

      return {
        reference: normalizedReference,
        trackingType,
        referenceKind,
        carrierScac: firstSeaShipment.carrier_scac?.trim().toUpperCase() || null,
      }
    }

    if (firstContainer) {
      return {
        reference: firstContainer,
        trackingType: 'container',
        referenceKind: 'CONTAINER',
        carrierScac: firstSeaShipment.carrier_scac?.trim().toUpperCase() || null,
      }
    }
  }

  // The air or sea leg's own reference. A courier's domestic package number is
  // never tracked at sea, whatever it looks like.
  const fallback = consignment.international_tracking_number || consignment.tracking_number || null
  const reference = fallback?.trim().toUpperCase() || null
  if (!reference) return null

  return {
    reference,
    trackingType: isContainerReference(reference) ? 'container' : 'booking',
    referenceKind: isContainerReference(reference) ? 'CONTAINER' : 'BOOKING',
    carrierScac: null,
  }
}

export function getConsignmentTrackingReference(consignment: TrackingConsignment) {
  return getConsignmentSeaTrackingTarget(consignment)?.reference ?? null
}

export function isContainerReference(reference: string | null | undefined) {
  return CONTAINER_REFERENCE_PATTERN.test((reference || '').trim().toUpperCase())
}

export function shouldLoadSeaTracking(
  consignment: Pick<
    Consignment,
    'mode' | 'shipping_method' | 'international_tracking_number' | 'tracking_number'
  > & {
    sea_shipments?: Array<{
      reference?: string | null
      containers?: Array<{ container_number?: string | null }>
    }>
  },
) {
  const target = getConsignmentSeaTrackingTarget(consignment)
  const shippingMethod = (consignment.shipping_method || consignment.mode || '').toUpperCase()

  return Boolean(target && (shippingMethod === 'SEA' || target.trackingType === 'container'))
}
